/**
 * SUB-4.1 · The merged "All Accounts" scope, as the client sees it.
 *
 * FR31/FR32: one scope covering the Agency Admin's parent account and all of its
 * sub-accounts. FR71: it is absent when it would mean nothing. FR43: it persists
 * exactly as an account selection does. FR33: every other surface still resolves
 * to one named account.
 *
 * ⚠️ NOTHING IS DECIDED HERE. Every field below is read from one server call,
 * `merged_scope_status()`, which derives all of it from the predicates
 * `20260813s` already made live (`may_enter_merged_scope`,
 * `merged_scope_tenant_ids`). This module parses that answer and asks questions
 * of it. It never computes availability, membership or the anchor from the
 * tenant list the client happens to hold: a second spelling of FR71 in the
 * browser is how the picker comes to offer an entry the server then refuses.
 *
 * ⚠️ THE ANCHOR IS HOME, NOT THE CURRENT ACCOUNT, which is why the status call
 * takes no argument. An Agency Admin sitting inside a client account still gets
 * All Accounts, anchored at their parent. A picker that passed
 * `currentTenant.tenant_id` would hide the entry from exactly the person and the
 * moment FR31 exists for, and `home_tenant_id()` is not granted to
 * `authenticated`, so the client could not compute the right anchor anyway.
 */

import { supabase } from '@/lib/supabaseClient';

export type ScopeMode = 'single' | 'subtree';

export interface MergedScopeStatus {
  /**
   * FR71. Whether All Accounts may be OFFERED: the caller holds the role over a
   * live anchor and that anchor has at least one live sub-account. Answered by
   * `may_enter_merged_scope()`, the same predicate `set_scope_mode` gates on, so
   * the entry is offered exactly when selecting it would succeed.
   */
  available: boolean;
  /** The account merged scope covers FROM. Null whenever it is not available. */
  anchorTenantId: string | null;
  /**
   * FR43. Whether the caller IS merged, right now.
   *
   * ⚠️ THIS IS AN OUTCOME, NOT A SETTING. The server derives it from what
   * `merged_scope_tenant_ids()` returned on that statement, never from the
   * stored `scope_mode` column. The two part company exactly when it matters: a
   * session row still saying 'subtree' after the Agency Admin grant was revoked,
   * the anchor archived, or the last sub-account detached resolves to one
   * account, and the header must then name that account rather than claim a
   * merged scope the caller no longer has.
   */
  scopeMode: ScopeMode;
  /** The accounts this request may read across. One element unless truly merged. */
  tenantIds: string[];
  /**
   * FR33 · D25. The single account every surface other than Projects, Tasks and
   * the pipeline navigator resolves to, Settings included.
   *
   * This is `get_current_user_tenant_id()`, which returns the session's
   * `active_tenant_id` whatever the scope mode says, and merged scope anchors
   * that at the parent. So the fallback account is the ANCHOR, and it already
   * was before this story: it is what all 641 unwidened policies and all 229
   * client `.eq('tenant_id')` filters have been resolving to since `20260813s`.
   * Carried here so the UI can NAME it instead of leaving it to be inferred.
   */
  singleAccountTenantId: string | null;
  /**
   * SUB-4.10 · NFR8. What this session ASKED for: the stored `scope_mode`
   * column, not the outcome. `20260814d` added it to the payload.
   *
   * ⚠️ THE ONE FIELD IN THIS INTERFACE THAT IS DELIBERATELY THE SETTING, and it
   * is here because D27's rule creates the gap it fills. Everything else reports
   * the outcome, so a merged scope that stopped being honoured is INDISTINGUISH-
   * ABLE from one that was never asked for — which is fine for the picker and is
   * exactly wrong for NFR8. On a fresh page load the client has no previous
   * reading to compare against, and FR43 says the All Accounts selection
   * persisted; without this field the user is served one account, told nothing,
   * and has every reason to believe they are looking at all of them.
   *
   * ⚠️ NEVER GATE ANYTHING ON IT. It is a claim the session stored, not an
   * authorization, and `merged_scope_tenant_ids()` revalidates it on every
   * statement precisely because it can be stale. It may only ever be used to
   * decide whether to TELL the user something.
   *
   * `null` means the server did not report it, which is true only while
   * `20260814d` is unapplied. Null is treated as "no claim" everywhere, so a
   * missing migration under-reports instead of nagging the whole product.
   */
  requestedScopeMode: ScopeMode | null;
  /**
   * Whether the four fields above are an ANSWER rather than a default.
   *
   * ⚠️ Same three-states-two-values trap as `isAgencyAdminResolved`, and it bit
   * that field first: `available: false` is both "no merged scope for you" and
   * "the call failed". A consumer that treats the second as the first hides All
   * Accounts from a real Agency Admin on one flaky request and nothing reports
   * it. Gates must treat `resolved === false` as unresolved, exactly as they
   * treat `isLoading`.
   */
  resolved: boolean;
}

/** The honest value before anything has been asked. */
export const UNRESOLVED_MERGED_SCOPE: MergedScopeStatus = {
  available: false,
  anchorTenantId: null,
  scopeMode: 'single',
  tenantIds: [],
  singleAccountTenantId: null,
  requestedScopeMode: null,
  resolved: false,
};

/**
 * Parse the `merged_scope_status()` payload.
 *
 * Returns null when the payload is not a readable answer, so the caller can keep
 * the previous state or stay unresolved rather than manufacture a denial out of
 * a malformed response. Every field is checked for its own type: a payload that
 * lost `tenant_ids` must not silently read as "merged over nothing".
 */
export function parseMergedScopeStatus(raw: unknown): MergedScopeStatus | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const row = raw as Record<string, unknown>;

  if (typeof row.available !== 'boolean') return null;

  const mode = row.scope_mode;
  if (mode !== 'single' && mode !== 'subtree') return null;

  const ids = Array.isArray(row.tenant_ids)
    ? row.tenant_ids.filter((id): id is string => typeof id === 'string')
    : null;
  if (!ids) return null;

  const asId = (value: unknown): string | null =>
    typeof value === 'string' && value.length > 0 ? value : null;

  // SUB-4.10. ⚠️ ABSENT IS NOT INVALID. Every other field above rejects the
  // whole payload when it is missing, because a merged scope reported over a
  // lost `tenant_ids` would read as "merged over nothing". This one is additive
  // and arrived a story later, so an old server must leave the client working
  // rather than unresolved. It reports LESS, and reporting less here means one
  // notice the user does not get, never a widening.
  const requested = row.requested_scope_mode;
  const requestedScopeMode: ScopeMode | null =
    requested === 'single' || requested === 'subtree' ? requested : null;

  return {
    available: row.available,
    anchorTenantId: asId(row.anchor_tenant_id),
    scopeMode: mode,
    tenantIds: ids,
    singleAccountTenantId: asId(row.single_account_tenant_id),
    requestedScopeMode,
    resolved: true,
  };
}

/**
 * AC1/AC2 · FR71. Should the picker render an All Accounts entry?
 *
 * Unresolved is not an offer. The row is absent while the answer is unknown,
 * which is the same direction the picker already fails in for `isAgencyAdmin`:
 * showing one account too few is recoverable, offering a scope the server then
 * refuses is a control that lies.
 */
export function offersMergedScope(status: MergedScopeStatus): boolean {
  return status.resolved && status.available;
}

/**
 * AC3 · FR43. Is the caller in merged scope on the state last read from the
 * server?
 *
 * The length test is belt and braces over a contract the server already keeps
 * (`scope_mode` is reported as 'subtree' only when the resolver returned more
 * than one account). Kept because this is the value the header renders from, and
 * "All Accounts" over a single account is precisely the unanswerable state FR33
 * exists to prevent.
 */
export function isMergedScope(status: MergedScopeStatus): boolean {
  return status.resolved && status.scopeMode === 'subtree' && status.tenantIds.length > 1;
}

/** How many accounts merged scope currently covers. Zero when not merged. */
export function mergedAccountCount(status: MergedScopeStatus): number {
  return isMergedScope(status) ? status.tenantIds.length : 0;
}

/**
 * SUB-4.6 · The accounts THIS request may read across, asked of the server.
 *
 * For services that are not React and so cannot read `TenantContext`. It is the
 * same one call the context makes; there is no second spelling of the rule here
 * either.
 *
 * ⚠️ RETURNS THE EMPTY ARRAY WHEN NOTHING COULD BE RESOLVED, and callers are
 * expected to fall back to the single account they already hold. Failure must
 * narrow, never widen: a merged list assembled because a status call failed is
 * the one direction there is no recovering from.
 */
export async function fetchScopeTenantIds(): Promise<string[]> {
  const { data, error } = await supabase.rpc('merged_scope_status');
  if (error) return [];

  const status = parseMergedScopeStatus(data);
  if (!status) return [];

  if (isMergedScope(status)) return status.tenantIds;
  return status.singleAccountTenantId ? [status.singleAccountTenantId] : [];
}

/**
 * A stable identity for the parts of the status a render depends on.
 *
 * Used to decide whether a re-read actually changed anything. The status is
 * re-fetched on focus, on visibility change and on a timer, and `merged_scope_status`
 * returns a fresh object every time; setting state unconditionally would push a
 * new context value into every consumer once a minute for no reason.
 */
export function mergedScopeKey(status: MergedScopeStatus): string {
  return [
    status.resolved ? '1' : '0',
    status.available ? '1' : '0',
    status.scopeMode,
    status.anchorTenantId ?? '-',
    status.singleAccountTenantId ?? '-',
    [...status.tenantIds].sort().join(','),
    // SUB-4.10. In `mergedScopeKey` and deliberately NOT in `scopeReachKey`
    // below. A second tab leaving merged scope changes what this session ASKED
    // for while this tab's reach is unchanged for one more poll, and the state
    // has to carry that or NFR8's notice is computed from a value the context
    // declined to store. It is not an invalidation trigger: nothing cached
    // became wrong because an intent changed.
    status.requestedScopeMode ?? '-',
  ].join('|');
}

/**
 * SUB-4.7 · FR74. The accounts this session may currently read across, as one
 * stable string. The identity of the SCOPE, not of the status payload.
 *
 * ⚠️ DELIBERATELY NARROWER THAN `mergedScopeKey`, and the difference is the
 * point. `mergedScopeKey` decides whether to RE-RENDER, so it carries
 * `available`: an Agency Admin who loses their last sub-account must lose the
 * picker row even though what they can read did not change. This key decides
 * whether to THROW CACHED DATA AWAY, so it must move only when the reach moves.
 * Keying invalidation on `mergedScopeKey` would drop every cache in the tree the
 * first time an offer appeared, and the AC2 clause it exists for is about
 * serving the wrong scope's rows, not about the picker.
 *
 * ⚠️ UNRESOLVED IS ITS OWN VALUE, never folded into single scope. "Not asked
 * yet" and "asked, and you are in one account" are the two states `resolved`
 * exists to keep apart, and an entry written under the first must not be served
 * under the second.
 *
 * Sorted, because the server happens to order by uuid and this must not depend
 * on that continuing to be true.
 */
export function scopeReachKey(status: MergedScopeStatus): string {
  if (!status.resolved) return 'unresolved';
  return [
    status.scopeMode,
    status.singleAccountTenantId ?? '-',
    [...status.tenantIds].sort().join(','),
  ].join('|');
}
